import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import { AuthService } from '../services/auth.service';
import { JwtService } from '../services/jwt.service';

interface DecodedToken {
  role_access?: { [key: string]: string[] };
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AdminGuard {
  private readonly ADMIN_ENTITY_ID = 'BWN-0';

  constructor(
    private router: Router,
    private authService: AuthService,
    private jwtService: JwtService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.isAuthenticated.pipe(
      take(1),
      map((isAuth) => {
        if (!isAuth) {
          this.router.navigate(['auth/login'], { queryParams: { returnUrl: state.url } });
          return false;
        }

        // Check for admin access (BWN-0 entity relation)
        if (!this.hasAdminAccess()) {
          this.router.navigate(['/']);
          return false;
        }

        return true;
      })
    );
  }

  hasAdminAccess(): boolean {
    try {
      const token = this.jwtService.getAccessToken();
      if (!token) return false;

      const decodedToken: DecodedToken = jwtDecode(token);

      // Check if role_access contains BWN-0 in any role
      if (decodedToken.role_access) {
        for (const roleId of Object.keys(decodedToken.role_access)) {
          const entities = decodedToken.role_access[roleId];
          if (entities && entities.includes(this.ADMIN_ENTITY_ID)) {
            return true;
          }
        }
      }

      return false;
    } catch {
      return false;
    }
  }
}
